import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ChevronLeft, 
  Calendar, 
  ChevronDown, 
  AlertTriangle,
  Check,
  User
} from "lucide-react";
import BookAppointmentModal from "./BookAppointmentModal";
import AddPhotosModal from "./AddPhotosModal";
import AddConsentFormModal from "./AddConsentFormModal";
import AddDocumentModal from "./AddDocumentModal";

// Static client data for demo - Comprehensive client profile
const clientData = {
  id: 1,
  name: "Emma Taylor",
  email: "emma.taylor@email.com",
  phone: "+44 7965 123456",
  dateOfBirth: "27/05/1988",
  address: "42 Kensington Gardens, London, SW7 4QY",
  joinedDate: "15 Jan 2024",
  avatar: null,
  occupation: "Marketing Director",
  referredBy: "Dr. Sarah Mitchell",
  preferredPractitioner: "Dr. Harper",
  stats: {
    appointments: 12,
    treatments: 8,
    totalSpend: 2850,
    consentsOk: true,
  },
  hasAllergy: true,
  allergyText: "Multiple allergies recorded - Lidocaine, Hyaluronic Acid sensitivity",
  recentAppointments: [
    { id: 1, date: "20 Apr 2024", treatment: "Botox - Upper Face", status: "Completed" },
    { id: 2, date: "17 Apr 2024", treatment: "Dermal Filler - Lips", status: "Completed" },
    { id: 3, date: "01 Apr 2024", treatment: "Follow-up Review", status: "Completed" },
    { id: 4, date: "15 Mar 2024", treatment: "Chemical Peel", status: "Completed" },
    { id: 5, date: "24 Apr 2024", treatment: "Botox - Touch Up", status: "Confirmed" },
    { id: 6, date: "7 May 2024", treatment: "Skin Consultation", status: "Pending" },
  ],
  recentTreatmentsWithPractitioner: [
    { id: 1, treatment: "Botox - Upper Face", practitioner: "Dr. Saleema", date: "20 Apr 2024" },
    { id: 2, treatment: "Dermal Filler - Lips", practitioner: "Emma Griffin", date: "17 Apr 2024" },
    { id: 3, treatment: "Chemical Peel", practitioner: "Dr. Harper", date: "15 Mar 2024" },
    { id: 4, treatment: "Microneedling", practitioner: "Emma Griffin", date: "01 Mar 2024" },
    { id: 5, treatment: "LED Light Therapy", practitioner: "Dr. Harper", date: "15 Feb 2024" },
  ],
  recentTreatments: [
    { id: 1, treatment: "Botox - Upper Face", date: "20 Apr 2024" },
    { id: 2, treatment: "Dermal Filler - Lips", date: "17 Apr 2024" },
    { id: 3, treatment: "Chemical Peel", date: "15 Mar 2024" },
    { id: 4, treatment: "Microneedling", date: "01 Mar 2024" },
  ],
  internalNotes: [
    "VIP client - always offer complimentary refreshments",
    "Prefers early morning or late afternoon appointments",
    "Has referred 5 new clients - consider loyalty discount",
    "Planning comprehensive rejuvenation treatment plan for 2024",
    "Sensitive to cold - warm treatment room before appointments",
  ],
  membershipTier: "Platinum",
  loyaltyPoints: 2850,
  communicationPreferences: {
    email: true,
    sms: true,
    phone: false,
    marketing: true,
  },
};

type TabType = "overview" | "appointments" | "treatment-history" | "consultations" | "clinical-notes" | "gallery" | "allergies" | "documents";

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const tabs: { id: TabType; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "appointments", label: "Appointments" },
    { id: "treatment-history", label: "Treatment History" },
    { id: "consultations", label: "Consultations" },
    { id: "clinical-notes", label: "Clinical Notes" },
    { id: "gallery", label: "Gallery" },
    { id: "allergies", label: "Allergies & Risks" },
    { id: "documents", label: "Documents & Consent" },
  ];

  const client = clientData; // In real app, fetch by id

  return (
    <div className="w-full min-h-screen bg-[#f8f5fc]">
      {/* Back Button */}
      <div className="px-6 pt-4">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#764BB7] hover:text-[#7a5a9e] text-sm font-medium transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Clients
        </button>
      </div>

      {/* Header */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left - Profile Info */}
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-white shadow-md">
              {client.avatar ? (
                <img src={client.avatar} alt={client.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-gray-400" />
              )}
            </div>
            
            {/* Name & Details */}
            <div>
              <h1 className="text-xl font-bold text-[#2d2d2d]">{client.name}</h1>
              <p className="text-sm text-[#6b6b6b]">{client.email}</p>
              <p className="text-xs text-[#764BB7]">Joined {client.joinedDate}</p>
            </div>
          </div>

          {/* Right - Actions */}
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsBookingModalOpen(true)}
              className="px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#7a5a9e] transition-colors"
            >
              Book Appointment
            </button>
            <button className="p-2 bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors">
              <Calendar className="w-5 h-5 text-[#6b6b6b]" />
            </button>
            <button className="p-2 bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors">
              <ChevronDown className="w-5 h-5 text-[#6b6b6b]" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 border-b border-[#e8e0f0] bg-white">
        <div className="flex gap-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-[#764BB7] text-[#764BB7]"
                  : "border-transparent text-[#6b6b6b] hover:text-[#764BB7]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === "overview" && (
          <OverviewTab client={client} />
        )}
        {activeTab === "appointments" && (
          <AppointmentsTab onBookAppointment={() => setIsBookingModalOpen(true)} />
        )}
        {activeTab === "treatment-history" && (
          <TreatmentHistoryTab />
        )}
        {activeTab === "consultations" && (
          <ConsultationsTab />
        )}
        {activeTab === "clinical-notes" && (
          <ClinicalNotesTab />
        )}
        {activeTab === "gallery" && (
          <GalleryTab />
        )}
        {activeTab === "allergies" && (
          <AllergiesTab />
        )}
        {activeTab === "documents" && (
          <DocumentsTab client={client} />
        )}
      </div>

      {/* Book Appointment Modal */}
      <BookAppointmentModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        client={{
          name: client.name,
          email: client.email,
          avatar: client.avatar,
        }}
        onSave={(data) => {
          console.log("Appointment saved:", data);
          // TODO: Handle appointment save
        }}
      />
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ client }: { client: typeof clientData }) {
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        {/* Appointments */}
        <div className="bg-white rounded-xl p-5 border border-[#e8e0f0]">
          <p className="text-sm text-[#6b6b6b] mb-1">Appointments</p>
          <p className="text-3xl font-bold text-[#2d2d2d]">{client.stats.appointments}</p>
        </div>

        {/* Treatments */}
        <div className="bg-white rounded-xl p-5 border border-[#e8e0f0]">
          <p className="text-sm text-[#6b6b6b] mb-1">Treatments</p>
          <p className="text-3xl font-bold text-[#2d2d2d]">{client.stats.treatments}</p>
        </div>

        {/* Total Spend */}
        <div className="bg-white rounded-xl p-5 border border-[#e8e0f0]">
          <p className="text-sm text-[#6b6b6b] mb-1">Total Spend (£)</p>
          <p className="text-3xl font-bold text-[#2d2d2d]">£{client.stats.totalSpend}</p>
        </div>

        {/* Consents */}
        <div className="bg-white rounded-xl p-5 border border-[#e8e0f0]">
          <p className="text-sm text-[#6b6b6b] mb-1">Consents</p>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-500" />
            <span className="text-lg font-semibold text-green-600">OK</span>
          </div>
        </div>
      </div>

      {/* Allergy Alert */}
      {client.hasAllergy && (
        <div className="bg-[#fff8e6] border border-[#ffd89e] rounded-xl px-5 py-3 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-[#e6a700]" />
          <span className="text-sm font-medium text-[#8a6d00]">{client.allergyText}</span>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Recent Appointments */}
          <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
            <h3 className="text-base font-semibold text-[#2d2d2d] px-5 py-4 border-b border-[#f0ebf5]">
              Recent Appointments
            </h3>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#f0ebf5]">
                  <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Date</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Treatment</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">
                    Status <ChevronDown className="inline w-3 h-3" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {client.recentAppointments.map((apt) => (
                  <tr key={apt.id} className="border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc]">
                    <td className="px-5 py-3 text-sm text-[#3d3d3d]">{apt.date}</td>
                    <td className="px-5 py-3 text-sm text-[#3d3d3d]">{apt.treatment}</td>
                    <td className="px-5 py-3">
                      <span className="text-xs font-medium text-[#e6a700] bg-[#fff8e6] px-2 py-1 rounded">
                        {apt.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Internal Notes */}
          <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
            <h3 className="text-base font-semibold text-[#2d2d2d] px-5 py-4 border-b border-[#f0ebf5]">
              Internal Notes
            </h3>
            <div className="p-5 space-y-3">
              {client.internalNotes.map((note, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-1 h-full min-h-[20px] bg-[#764BB7] rounded-full" />
                  <p className="text-sm text-[#5c5c5c]">{note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Recent Treatments with Practitioner */}
          <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
            <h3 className="text-base font-semibold text-[#2d2d2d] px-5 py-4 border-b border-[#f0ebf5]">
              Recent Treatments
            </h3>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#f0ebf5] bg-[#f8f5fc]">
                  <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Treatment</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Practitioner</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">
                    Date <ChevronDown className="inline w-3 h-3" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {client.recentTreatmentsWithPractitioner.map((treatment) => (
                  <tr key={treatment.id} className="border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc]">
                    <td className="px-5 py-3 text-sm text-[#3d3d3d]">{treatment.treatment}</td>
                    <td className="px-5 py-3 text-sm text-[#6b6b6b]">{treatment.practitioner}</td>
                    <td className="px-5 py-3 text-sm text-[#3d3d3d]">{treatment.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent Treatments Simple List */}
          <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
            <h3 className="text-base font-semibold text-[#2d2d2d] px-5 py-4 border-b border-[#f0ebf5]">
              Recent Treatments
            </h3>
            <div className="divide-y divide-[#f0ebf5]">
              {client.recentTreatments.map((treatment) => (
                <div key={treatment.id} className="px-5 py-3 flex items-center justify-between hover:bg-[#faf8fc]">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-[#764BB7] rounded-full" />
                    <span className="text-sm text-[#3d3d3d]">{treatment.treatment}</span>
                  </div>
                  <span className="text-sm text-[#6b6b6b]">{treatment.date}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Appointments Tab Component
type AppointmentSubTab = "upcoming" | "all" | "completed" | "cancelled";
type AppointmentStatus = "Confirmed" | "Pending" | "Completed" | "Cancelled";

interface Appointment {
  id: number;
  date: string;
  time: string;
  treatment: string;
  practitioner: string | null;
  status: AppointmentStatus;
}

function AppointmentsTab({ onBookAppointment }: { onBookAppointment: () => void }) {
  const [activeSubTab, setActiveSubTab] = useState<AppointmentSubTab>("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [openActionsId, setOpenActionsId] = useState<number | null>(null);

  // All appointments data
  const allAppointments: Appointment[] = [
    { id: 1, date: "24 Apr 2026", time: "11:30 am", treatment: "Lip Filler", practitioner: "Dr. Harper", status: "Confirmed" },
    { id: 2, date: "7 May 2026", time: "3:00 pm", treatment: "Botox", practitioner: "Dr. Parker", status: "Confirmed" },
    { id: 3, date: "17 May 2026", time: "10:00 am", treatment: "Lip Filler", practitioner: "Dr. Harper", status: "Confirmed" },
    { id: 4, date: "3 Jun 2026", time: "", treatment: "Botox", practitioner: null, status: "Pending" },
    { id: 5, date: "17 Jun 2026", time: "1:30 pm", treatment: "Botox", practitioner: "Dr. Parker", status: "Confirmed" },
    { id: 6, date: "11 Mar 2026", time: "11:00 am", treatment: "Botox", practitioner: "Dr. Harper", status: "Completed" },
    { id: 7, date: "17 Feb 2026", time: "3:00 pm", treatment: "Lip Filler", practitioner: "Dr. Harper", status: "Completed" },
    { id: 8, date: "20 Dec 2025", time: "2:00 pm", treatment: "Chemical Peel", practitioner: "Sarah M.", status: "Cancelled" },
    { id: 9, date: "15 Nov 2025", time: "11:30 am", treatment: "Microneedling", practitioner: "Dr. Harper", status: "Cancelled" },
  ];

  const subTabs: { id: AppointmentSubTab; label: string }[] = [
    { id: "upcoming", label: "Upcoming" },
    { id: "all", label: "All" },
    { id: "completed", label: "Completed" },
    { id: "cancelled", label: "Cancelled" },
  ];

  // Filter appointments based on active sub-tab
  const getFilteredByTab = () => {
    switch (activeSubTab) {
      case "upcoming":
        return allAppointments.filter(apt => apt.status === "Confirmed" || apt.status === "Pending");
      case "completed":
        return allAppointments.filter(apt => apt.status === "Completed");
      case "cancelled":
        return allAppointments.filter(apt => apt.status === "Cancelled");
      case "all":
      default:
        return allAppointments;
    }
  };

  // Apply search and status filter
  const filteredAppointments = getFilteredByTab().filter(apt => {
    const matchesSearch = searchQuery === "" || 
      apt.treatment.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (apt.practitioner && apt.practitioner.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = statusFilter === "all" || apt.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: AppointmentStatus) => {
    switch (status) {
      case "Confirmed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-600">
            <span className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-white" />
            </span>
            Confirmed
          </span>
        );
      case "Pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600">
            <span className="w-4 h-4 rounded-full border-2 border-amber-400 flex items-center justify-center">
              <span className="w-1 h-1 bg-amber-400 rounded-full"></span>
            </span>
            Pending
          </span>
        );
      case "Completed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            <span className="w-4 h-4 rounded-full bg-gray-400 flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-white" />
            </span>
            Completed
          </span>
        );
      case "Cancelled":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600">
            <span className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-white text-[10px] font-bold">×</span>
            Cancelled
          </span>
        );
    }
  };

  const toggleActions = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenActionsId(openActionsId === id ? null : id);
  };

  return (
    <div className="space-y-6">
      {/* Header with Title and Search/Filters */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#2d2d2d]">Appointments</h2>
        
        {/* Search and Filters */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 w-48 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>

          {/* Status Filter Dropdown */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all cursor-pointer"
            >
              <option value="all">Upcoming</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Sub-Tabs */}
      <div className="flex items-center gap-1 bg-[#f0ebf5] p-1 rounded-lg w-fit">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeSubTab === tab.id
                ? "bg-white text-[#704aaf] shadow-sm"
                : "text-[#6b6b6b] hover:text-[#704aaf]"
            }`}
          >
            {tab.label}
            {tab.id === "upcoming" && activeSubTab === "upcoming" && (
              <span className="ml-1 text-[#704aaf]">»</span>
            )}
          </button>
        ))}
      </div>

      {/* Appointments Table */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#f0ebf5] bg-[#faf8fc]">
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Date</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Time</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Treatment</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Practitioner</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Status</th>
              <th className="text-right px-5 py-3 text-xs font-medium text-[#6b6b6b]"></th>
            </tr>
          </thead>
          <tbody>
            {filteredAppointments.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-gray-500">
                  <p className="text-sm">No appointments found</p>
                </td>
              </tr>
            ) : (
              filteredAppointments.map((apt) => (
                <tr key={apt.id} className="border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc] transition-colors">
                  <td className="px-5 py-4 text-sm font-medium text-[#3d3d3d]">{apt.date}</td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{apt.time || "--"}</td>
                  <td className="px-5 py-4 text-sm text-[#3d3d3d]">{apt.treatment}</td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{apt.practitioner || "--"}</td>
                  <td className="px-5 py-4">{getStatusBadge(apt.status)}</td>
                  <td className="px-5 py-4 text-right relative">
                    <button
                      onClick={(e) => toggleActions(apt.id, e)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="6" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="18" r="1.5" />
                      </svg>
                    </button>
                    {openActionsId === apt.id && (
                      <div className="absolute top-full right-5 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 transition-colors">
                          View appointment
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 transition-colors">
                          Edit appointment
                        </button>
                        {(apt.status === "Confirmed" || apt.status === "Pending") && (
                          <>
                            <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 transition-colors">
                              Reschedule
                            </button>
                            <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors">
                              Cancel
                            </button>
                          </>
                        )}
                        {apt.status === "Confirmed" && (
                          <button className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 transition-colors">
                            Mark as completed
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Click outside to close dropdown */}
      {openActionsId && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setOpenActionsId(null)}
        />
      )}
    </div>
  );
}

// Treatment History Tab Component
type TreatmentFilterTab = "all" | "botox" | "dermal-filler" | "skin-booster" | "microneedling" | "laser" | "prp";
type TreatmentStatus = "Signed" | "Completed" | "Pending";
type PaymentStatus = "Paid" | "Unpaid" | "Partially Paid";

interface TreatmentRecord {
  id: number;
  date: string;
  treatment: string;
  subTreatment?: string;
  practitioner: string;
  consultation: string;
  status: TreatmentStatus;
  consentSigned: boolean;
  price: number;
  paymentStatus: PaymentStatus;
}

function TreatmentHistoryTab() {
  const [activeFilter, setActiveFilter] = useState<TreatmentFilterTab>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const filterTabs: { id: TreatmentFilterTab; label: string }[] = [
    { id: "all", label: "All" },
    { id: "botox", label: "Botox" },
    { id: "dermal-filler", label: "Dermal Filler" },
    { id: "skin-booster", label: "Skin Booster" },
    { id: "microneedling", label: "Microneedling" },
    { id: "laser", label: "Laser" },
    { id: "prp", label: "PRP" },
  ];

  const treatmentRecords: TreatmentRecord[] = [
    {
      id: 1,
      date: "Apr 20 2024",
      treatment: "Botox – Upper Face",
      practitioner: "Dr Saleema",
      consultation: "20 Apr 2024",
      status: "Signed",
      consentSigned: true,
      price: 180,
      paymentStatus: "Paid",
    },
    {
      id: 2,
      date: "Apr 17, 2024",
      treatment: "Dermal Filler –",
      subTreatment: "- Lip Filler",
      practitioner: "Emma Griffin",
      consultation: "17 Apr 2024",
      status: "Completed",
      consentSigned: false,
      price: 250,
      paymentStatus: "Paid",
    },
    {
      id: 3,
      date: "Dec 15, 2023",
      treatment: "Botox – Crow's Feet",
      practitioner: "Emma Griffin",
      consultation: "N/A",
      status: "Pending",
      consentSigned: true,
      price: 180,
      paymentStatus: "Partially Paid",
    },
    {
      id: 4,
      date: "Oct 02, 2023",
      treatment: "Microneedling",
      practitioner: "Dr Harper",
      consultation: "N/A",
      status: "Completed",
      consentSigned: false,
      price: 250,
      paymentStatus: "Unpaid",
    },
    {
      id: 5,
      date: "Sep 14, 2023",
      treatment: "Dermal Filler –",
      subTreatment: "- Lip Filler",
      practitioner: "Emma Griffin",
      consultation: "14 Sep 2023",
      status: "Completed",
      consentSigned: false,
      price: 250,
      paymentStatus: "Paid",
    },
  ];

  // Filter treatments based on active filter tab and search
  const filteredTreatments = treatmentRecords.filter((record) => {
    const matchesFilter = activeFilter === "all" || 
      record.treatment.toLowerCase().includes(activeFilter.replace("-", " "));
    const matchesSearch = searchQuery === "" ||
      record.treatment.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.practitioner.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const totalPages = Math.ceil(filteredTreatments.length / itemsPerPage);
  const paginatedTreatments = filteredTreatments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status: TreatmentStatus) => {
    switch (status) {
      case "Signed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-600 border border-green-100">
            <Check className="w-3 h-3" />
            Signed
          </span>
        );
      case "Completed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-600 border border-green-100">
            <Check className="w-3 h-3" />
            Completed
          </span>
        );
      case "Pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-600 border border-amber-100">
            <User className="w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  const getPaymentBadge = (status: PaymentStatus) => {
    switch (status) {
      case "Paid":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
            Paid
          </span>
        );
      case "Unpaid":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-600 border border-red-100">
            Unpaid
          </span>
        );
      case "Partially Paid":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-amber-50 text-amber-600 border border-amber-100">
            Partially Paid
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[#2d2d2d]">Treatment History</h2>
        <p className="text-sm text-[#6b6b6b] mt-1">
          Detailed history of all completed treatments across appointments
        </p>
      </div>

      {/* Filter Tabs and Search */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Treatment Type Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveFilter(tab.id);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeFilter === tab.id
                  ? "bg-[#704aaf] text-white"
                  : "bg-white text-[#5c5c5c] border border-[#e8e0f0] hover:bg-[#f8f5fc]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Date Range and Search */}
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 border border-[#e8e0f0] rounded-lg text-sm font-medium text-[#5c5c5c] bg-white hover:bg-[#f8f5fc] transition-colors">
            <Calendar className="w-4 h-4" />
            Date Range
            <ChevronDown className="w-4 h-4" />
          </button>
          <div className="relative">
            <input
              type="text"
              placeholder="Search treatments..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-4 pr-4 py-2 w-48 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Date Header Row */}
      <div className="flex items-center justify-between bg-[#f8f5fc] rounded-lg px-5 py-3">
        <span className="text-sm font-semibold text-[#2d2d2d]">Date</span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#5c5c5c]">{currentPage}</span>
          <div className="flex items-center gap-1">
            <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">−</button>
            <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">≡</button>
            <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">⊞</button>
          </div>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
          >
            &lt;
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Treatment Table */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#f0ebf5] bg-[#faf8fc]">
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Date</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Treatment</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Practitioner</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Consultation</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Status</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Consent</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Price (£)</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Payment</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTreatments.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-12 text-center text-gray-500">
                  <p className="text-sm">No treatments found</p>
                </td>
              </tr>
            ) : (
              paginatedTreatments.map((record) => (
                <tr key={record.id} className="border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc] transition-colors">
                  <td className="px-5 py-4 text-sm text-[#3d3d3d]">{record.date}</td>
                  <td className="px-5 py-4">
                    <div>
                      <p className="text-sm font-medium text-[#2d2d2d]">{record.treatment}</p>
                      {record.subTreatment && (
                        <p className="text-xs text-[#6b6b6b]">{record.subTreatment}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{record.practitioner}</td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{record.consultation}</td>
                  <td className="px-5 py-4">{getStatusBadge(record.status)}</td>
                  <td className="px-5 py-4">
                    {record.consentSigned ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-600 border border-green-100">
                        <Check className="w-3 h-3" />
                        Signed
                      </span>
                    ) : (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm font-medium text-[#2d2d2d]">£{record.price}</td>
                  <td className="px-5 py-4">{getPaymentBadge(record.paymentStatus)}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      <button className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex items-center justify-end text-sm text-[#6b6b6b]">
        <span>
          {(currentPage - 1) * itemsPerPage + 1} – {Math.min(currentPage * itemsPerPage, filteredTreatments.length)} of {filteredTreatments.length} treatments
        </span>
        <div className="flex items-center gap-1 ml-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
          >
            &lt;
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  );
}

// Consultations Tab Component
type ConsultationFilterTab = "all" | "medical" | "consent" | "patch-test" | "follow-up";
type ConsultationStatus = "Completed" | "Pending" | "Expired" | "Signed";

interface ConsultationRecord {
  id: number;
  date: string;
  type: string;
  formName: string;
  linkedTreatment?: string;
  practitioner: string;
  status: ConsultationStatus;
  signedDate?: string;
  expiryDate?: string;
}

function ConsultationsTab() {
  const [activeFilter, setActiveFilter] = useState<ConsultationFilterTab>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const filterTabs: { id: ConsultationFilterTab; label: string }[] = [
    { id: "all", label: "All" },
    { id: "medical", label: "Medical Questionnaire" },
    { id: "consent", label: "Consent Forms" },
    { id: "patch-test", label: "Patch Tests" },
    { id: "follow-up", label: "Follow-up Reviews" },
  ];

  const consultationRecords: ConsultationRecord[] = [
    {
      id: 1,
      date: "20 Apr 2024",
      type: "consent",
      formName: "Botox Consent Form",
      linkedTreatment: "Botox – Upper Face",
      practitioner: "Dr Saleema",
      status: "Signed",
      signedDate: "20 Apr 2024",
      expiryDate: "20 Apr 2025",
    },
    {
      id: 2,
      date: "17 Apr 2024",
      type: "consent",
      formName: "Dermal Filler Consent",
      linkedTreatment: "Dermal Filler – Lips",
      practitioner: "Emma Griffin",
      status: "Signed",
      signedDate: "17 Apr 2024",
      expiryDate: "17 Apr 2025",
    },
    {
      id: 3,
      date: "15 Apr 2024",
      type: "medical",
      formName: "Medical History Questionnaire",
      practitioner: "Emma Griffin",
      status: "Completed",
      signedDate: "15 Apr 2024",
    },
    {
      id: 4,
      date: "10 Apr 2024",
      type: "patch-test",
      formName: "Skin Sensitivity Patch Test",
      linkedTreatment: "Chemical Peel",
      practitioner: "Dr Harper",
      status: "Completed",
      signedDate: "10 Apr 2024",
    },
    {
      id: 5,
      date: "01 Mar 2024",
      type: "follow-up",
      formName: "2-Week Follow-up Review",
      linkedTreatment: "Lip Filler",
      practitioner: "Emma Griffin",
      status: "Completed",
      signedDate: "01 Mar 2024",
    },
    {
      id: 6,
      date: "15 Feb 2024",
      type: "consent",
      formName: "Lip Filler Consent Form",
      linkedTreatment: "Lip Filler",
      practitioner: "Emma Griffin",
      status: "Expired",
      signedDate: "15 Feb 2023",
      expiryDate: "15 Feb 2024",
    },
    {
      id: 7,
      date: "10 Feb 2024",
      type: "medical",
      formName: "Allergy Assessment Form",
      practitioner: "Dr Harper",
      status: "Pending",
    },
  ];

  // Filter consultations based on active filter tab and search
  const filteredConsultations = consultationRecords.filter((record) => {
    const matchesFilter = activeFilter === "all" || record.type === activeFilter;
    const matchesSearch = searchQuery === "" ||
      record.formName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.practitioner.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (record.linkedTreatment && record.linkedTreatment.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const totalPages = Math.ceil(filteredConsultations.length / itemsPerPage);
  const paginatedConsultations = filteredConsultations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status: ConsultationStatus) => {
    switch (status) {
      case "Signed":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-green-50 text-green-600 border border-green-100">
            <Check className="w-3 h-3" />
            Signed
          </span>
        );
      case "Completed":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100">
            <Check className="w-3 h-3" />
            Completed
          </span>
        );
      case "Pending":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-amber-50 text-amber-600 border border-amber-100">
            <AlertTriangle className="w-3 h-3" />
            Pending
          </span>
        );
      case "Expired":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-600 border border-red-100">
            <AlertTriangle className="w-3 h-3" />
            Expired
          </span>
        );
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "medical":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-[#704aaf]">
            Medical
          </span>
        );
      case "consent":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">
            Consent
          </span>
        );
      case "patch-test":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-600">
            Patch Test
          </span>
        );
      case "follow-up":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-50 text-teal-600">
            Follow-up
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[#2d2d2d]">Consultations</h2>
        <p className="text-sm text-[#6b6b6b] mt-1">
          Complete log of all consultations, questionnaires, and consent forms
        </p>
      </div>

      {/* Filter Tabs and Search */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Consultation Type Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveFilter(tab.id);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeFilter === tab.id
                  ? "bg-[#704aaf] text-white"
                  : "bg-white text-[#5c5c5c] border border-[#e8e0f0] hover:bg-[#f8f5fc]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Date Range and Search */}
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 border border-[#e8e0f0] rounded-lg text-sm font-medium text-[#5c5c5c] bg-white hover:bg-[#f8f5fc] transition-colors">
            <Calendar className="w-4 h-4" />
            Date Range
            <ChevronDown className="w-4 h-4" />
          </button>
          <div className="relative">
            <input
              type="text"
              placeholder="Search consultations..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 pr-4 py-2 w-52 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-[#e8e0f0]">
          <p className="text-xs text-[#6b6b6b] mb-1">Total Consultations</p>
          <p className="text-2xl font-bold text-[#2d2d2d]">{consultationRecords.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#e8e0f0]">
          <p className="text-xs text-[#6b6b6b] mb-1">Signed/Completed</p>
          <p className="text-2xl font-bold text-green-600">
            {consultationRecords.filter(c => c.status === "Signed" || c.status === "Completed").length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#e8e0f0]">
          <p className="text-xs text-[#6b6b6b] mb-1">Pending</p>
          <p className="text-2xl font-bold text-amber-600">
            {consultationRecords.filter(c => c.status === "Pending").length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#e8e0f0]">
          <p className="text-xs text-[#6b6b6b] mb-1">Expired</p>
          <p className="text-2xl font-bold text-red-600">
            {consultationRecords.filter(c => c.status === "Expired").length}
          </p>
        </div>
      </div>

      {/* Consultations Table */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#f0ebf5] bg-[#faf8fc]">
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Date</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Type</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Form Name</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Linked Treatment</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Practitioner</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Status</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Signed Date</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedConsultations.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-12 text-center text-gray-500">
                  <p className="text-sm">No consultations found</p>
                </td>
              </tr>
            ) : (
              paginatedConsultations.map((record) => (
                <tr key={record.id} className="border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc] transition-colors">
                  <td className="px-5 py-4 text-sm text-[#3d3d3d]">{record.date}</td>
                  <td className="px-5 py-4">{getTypeBadge(record.type)}</td>
                  <td className="px-5 py-4">
                    <p className="text-sm font-medium text-[#2d2d2d]">{record.formName}</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{record.linkedTreatment || "—"}</td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{record.practitioner}</td>
                  <td className="px-5 py-4">{getStatusBadge(record.status)}</td>
                  <td className="px-5 py-4">
                    <div>
                      <p className="text-sm text-[#3d3d3d]">{record.signedDate || "—"}</p>
                      {record.expiryDate && (
                        <p className="text-xs text-[#9b9b9b]">Expires: {record.expiryDate}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      <button 
                        className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors"
                        title="View"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button 
                        className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors"
                        title="Download PDF"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                      {record.status === "Pending" && (
                        <button 
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                          title="Send Reminder"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </button>
                      )}
                      {record.status === "Expired" && (
                        <button 
                          className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors"
                          title="Renew"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#6b6b6b]">
          Showing {(currentPage - 1) * itemsPerPage + 1} – {Math.min(currentPage * itemsPerPage, filteredConsultations.length)} of {filteredConsultations.length} consultations
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 text-sm border border-[#e8e0f0] rounded-lg text-[#5c5c5c] hover:bg-[#f8f5fc] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm font-medium text-[#704aaf] bg-purple-50 rounded-lg">
            {currentPage}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="px-3 py-1.5 text-sm border border-[#e8e0f0] rounded-lg text-[#5c5c5c] hover:bg-[#f8f5fc] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

// Allergies & Risks Tab Component
type AllergySeverity = "Mild" | "Moderate" | "Severe" | "Life-threatening";
type AllergyStatus = "Active" | "Inactive";

interface AllergyRecord {
  id: number;
  allergyName: string;
  reaction: string;
  severity: AllergySeverity;
  notes?: string;
  status: AllergyStatus;
  dateRecorded: string;
  recordedBy: string;
}

function AllergiesTab() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const allergies: AllergyRecord[] = [
    {
      id: 1,
      allergyName: "Lidocaine",
      reaction: "Swelling, difficulty breathing",
      severity: "Severe",
      notes: "Discovered during dental procedure in 2019. Use alternative anaesthetic.",
      status: "Active",
      dateRecorded: "15 Jan 2024",
      recordedBy: "Dr Harper",
    },
    {
      id: 2,
      allergyName: "Hyaluronic Acid",
      reaction: "Localised swelling, redness",
      severity: "Moderate",
      notes: "Mild reaction observed. Proceed with caution, use test dose first.",
      status: "Active",
      dateRecorded: "20 Feb 2024",
      recordedBy: "Emma Griffin",
    },
    {
      id: 3,
      allergyName: "Latex",
      reaction: "Skin irritation, rash",
      severity: "Mild",
      notes: "Use latex-free gloves during all procedures.",
      status: "Active",
      dateRecorded: "10 Mar 2024",
      recordedBy: "Dr Saleema",
    },
    {
      id: 4,
      allergyName: "Penicillin",
      reaction: "Hives, anaphylaxis risk",
      severity: "Life-threatening",
      notes: "Do NOT prescribe any penicillin-based antibiotics. Alternative: Erythromycin",
      status: "Active",
      dateRecorded: "05 Jan 2024",
      recordedBy: "Dr Harper",
    },
    {
      id: 5,
      allergyName: "Aspirin",
      reaction: "Stomach upset",
      severity: "Mild",
      notes: "Patient reports mild reaction, now tolerates. Kept for records.",
      status: "Inactive",
      dateRecorded: "01 Dec 2023",
      recordedBy: "Emma Griffin",
    },
  ];

  const filteredAllergies = allergies.filter((allergy) => {
    const matchesSearch = searchQuery === "" ||
      allergy.allergyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      allergy.reaction.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = showInactive || allergy.status === "Active";
    return matchesSearch && matchesStatus;
  });

  const activeAllergies = allergies.filter(a => a.status === "Active");
  const severeAllergies = activeAllergies.filter(a => a.severity === "Severe" || a.severity === "Life-threatening");

  const getSeverityBadge = (severity: AllergySeverity) => {
    switch (severity) {
      case "Mild":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-600 border border-green-100">
            Mild
          </span>
        );
      case "Moderate":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-100">
            Moderate
          </span>
        );
      case "Severe":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-600 border border-orange-100">
            Severe
          </span>
        );
      case "Life-threatening":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-100">
            ⚠️ Life-threatening
          </span>
        );
    }
  };

  const getStatusBadge = (status: AllergyStatus) => {
    if (status === "Active") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-600">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
        Inactive
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#2d2d2d]">Allergies & Risks</h2>
          <p className="text-sm text-[#6b6b6b] mt-1">
            Manage patient allergies and medical risks – displayed across all treatments
          </p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#5f3d9a] transition-colors"
        >
          + Add Allergy
        </button>
      </div>

      {/* Critical Alert Banner */}
      {severeAllergies.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800">Critical Allergies Alert</h3>
              <p className="text-sm text-red-700 mt-1">
                This patient has {severeAllergies.length} severe/life-threatening {severeAllergies.length === 1 ? 'allergy' : 'allergies'}:
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {severeAllergies.map((allergy) => (
                  <span
                    key={allergy.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800"
                  >
                    ⚠️ {allergy.allergyName}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-[#e8e0f0]">
          <p className="text-xs text-[#6b6b6b] mb-1">Total Allergies</p>
          <p className="text-2xl font-bold text-[#2d2d2d]">{allergies.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#e8e0f0]">
          <p className="text-xs text-[#6b6b6b] mb-1">Active</p>
          <p className="text-2xl font-bold text-green-600">{activeAllergies.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#e8e0f0]">
          <p className="text-xs text-[#6b6b6b] mb-1">Severe / Critical</p>
          <p className="text-2xl font-bold text-red-600">{severeAllergies.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#e8e0f0]">
          <p className="text-xs text-[#6b6b6b] mb-1">Inactive</p>
          <p className="text-2xl font-bold text-gray-500">
            {allergies.filter(a => a.status === "Inactive").length}
          </p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <input
            type="text"
            placeholder="Search allergies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 w-64 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#5c5c5c] cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 text-[#704aaf] border-gray-300 rounded focus:ring-purple-200"
          />
          Show inactive allergies
        </label>
      </div>

      {/* Allergies Table */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#f0ebf5] bg-[#faf8fc]">
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Allergy Name</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Reaction</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Severity</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Notes</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Status</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Recorded</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAllergies.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-3">
                      <Check className="w-6 h-6 text-green-500" />
                    </div>
                    <p className="text-sm font-medium text-[#3d3d3d]">No allergies recorded</p>
                    <p className="text-xs text-[#6b6b6b] mt-1">This patient has no known allergies on file</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredAllergies.map((allergy) => (
                <tr 
                  key={allergy.id} 
                  className={`border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc] transition-colors ${
                    allergy.status === "Inactive" ? "opacity-60" : ""
                  }`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      {(allergy.severity === "Severe" || allergy.severity === "Life-threatening") && (
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                      <span className="text-sm font-semibold text-[#2d2d2d]">{allergy.allergyName}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c] max-w-[200px]">{allergy.reaction}</td>
                  <td className="px-5 py-4">{getSeverityBadge(allergy.severity)}</td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-[#5c5c5c] max-w-[250px] truncate" title={allergy.notes}>
                      {allergy.notes || "—"}
                    </p>
                  </td>
                  <td className="px-5 py-4">{getStatusBadge(allergy.status)}</td>
                  <td className="px-5 py-4">
                    <div>
                      <p className="text-sm text-[#3d3d3d]">{allergy.dateRecorded}</p>
                      <p className="text-xs text-[#9b9b9b]">by {allergy.recordedBy}</p>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      <button 
                        className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      {allergy.status === "Active" ? (
                        <button 
                          className="p-1.5 text-gray-400 hover:bg-gray-100 rounded transition-colors"
                          title="Mark as Inactive"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                        </button>
                      ) : (
                        <button 
                          className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors"
                          title="Mark as Active"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      )}
                      <button 
                        className="p-1.5 text-red-400 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Info Note */}
      <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-[#704aaf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-[#704aaf]">Why this matters</h4>
            <p className="text-sm text-[#5c5c5c] mt-1">
              Allergies recorded here are automatically displayed on the Timeline, Treatment headers, and consent forms. 
              This prevents dangerous duplication and ensures all practitioners are aware of patient risks.
            </p>
          </div>
        </div>
      </div>

      {/* Add Allergy Modal */}
      {isAddModalOpen && (
        <AddAllergyModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={(data) => {
            console.log("Allergy saved:", data);
          }}
        />
      )}
    </div>
  );
}

// Add Allergy Modal Component
interface AddAllergyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: any) => void;
}

function AddAllergyModal({ isOpen, onClose, onSave }: AddAllergyModalProps) {
  const [allergyName, setAllergyName] = useState("");
  const [reaction, setReaction] = useState("");
  const [severity, setSeverity] = useState<AllergySeverity>("Mild");
  const [notes, setNotes] = useState("");

  const handleSave = () => {
    const data = {
      allergyName,
      reaction,
      severity,
      notes,
      status: "Active",
      dateRecorded: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    };
    onSave?.(data);
    // Reset form
    setAllergyName("");
    setReaction("");
    setSeverity("Mild");
    setNotes("");
    onClose();
  };

  const isValid = allergyName && reaction;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-purple-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-red-50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="text-lg font-bold text-[#2d2d2d]">Add Allergy</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-red-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          {/* Allergy Name */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Allergy Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={allergyName}
              onChange={(e) => setAllergyName(e.target.value)}
              placeholder="e.g., Lidocaine, Penicillin, Latex"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>

          {/* Reaction */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Reaction <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={reaction}
              onChange={(e) => setReaction(e.target.value)}
              placeholder="Describe the allergic reaction"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>

          {/* Severity */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Severity
            </label>
            <div className="grid grid-cols-4 gap-2">
              {(["Mild", "Moderate", "Severe", "Life-threatening"] as AllergySeverity[]).map((sev) => (
                <button
                  key={sev}
                  onClick={() => setSeverity(sev)}
                  className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                    severity === sev
                      ? sev === "Life-threatening"
                        ? "bg-red-100 border-red-300 text-red-700"
                        : sev === "Severe"
                        ? "bg-orange-100 border-orange-300 text-orange-700"
                        : sev === "Moderate"
                        ? "bg-amber-100 border-amber-300 text-amber-700"
                        : "bg-green-100 border-green-300 text-green-700"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {sev === "Life-threatening" ? "Critical" : sev}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes or instructions..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <AlertTriangle className="w-4 h-4" />
              Save Allergy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Clinical Notes Tab Component
type NoteType = "treatment" | "general" | "follow-up" | "consultation" | "internal";
type NoteFilterTab = "all" | "treatment" | "general" | "follow-up" | "internal";

interface ClinicalNote {
  id: number;
  date: string;
  time: string;
  type: NoteType;
  title: string;
  content: string;
  practitioner: string;
  linkedTreatment?: string;
  linkedAppointment?: string;
  isPinned?: boolean;
  isPrivate?: boolean;
}

function ClinicalNotesTab() {
  const [activeFilter, setActiveFilter] = useState<NoteFilterTab>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"cards" | "list">("cards");

  const filterTabs: { id: NoteFilterTab; label: string }[] = [
    { id: "all", label: "All Notes" },
    { id: "treatment", label: "Treatment Notes" },
    { id: "general", label: "General Notes" },
    { id: "follow-up", label: "Follow-up Notes" },
    { id: "internal", label: "Internal Notes" },
  ];

  const clinicalNotes: ClinicalNote[] = [
    {
      id: 1,
      date: "20 Apr 2024",
      time: "14:30",
      type: "treatment",
      title: "Botox Treatment - Upper Face",
      content: "Patient tolerated procedure well. 20 units administered to forehead, 12 units to glabella. No adverse reactions observed. Advised patient to avoid lying down for 4 hours and not to massage treated area. Follow-up scheduled in 2 weeks.",
      practitioner: "Dr Saleema",
      linkedTreatment: "Botox – Upper Face",
      isPinned: true,
    },
    {
      id: 2,
      date: "17 Apr 2024",
      time: "11:15",
      type: "treatment",
      title: "Lip Filler - Initial Consultation & Treatment",
      content: "Patient requested subtle lip enhancement. 0.5ml Juvederm Ultra administered to upper and lower lip. Patient happy with results. Discussed potential for top-up in 2-4 weeks if desired. Ice pack applied post-procedure.",
      practitioner: "Emma Griffin",
      linkedTreatment: "Dermal Filler – Lips",
    },
    {
      id: 3,
      date: "15 Apr 2024",
      time: "09:45",
      type: "general",
      title: "Medical History Update",
      content: "Patient disclosed new medication - started on blood pressure medication (Amlodipine 5mg) since last visit. Updated medical records accordingly. No contraindications for planned aesthetic treatments. Will monitor for any bruising tendency.",
      practitioner: "Emma Griffin",
      isPinned: true,
    },
    {
      id: 4,
      date: "01 Apr 2024",
      time: "16:00",
      type: "follow-up",
      title: "2-Week Follow-up - Lip Filler",
      content: "Patient attended 2-week follow-up. No complications, swelling fully resolved. Patient very satisfied with results. Discussed maintenance schedule - recommended review in 6-9 months. Before/after photos taken with consent.",
      practitioner: "Emma Griffin",
      linkedTreatment: "Dermal Filler – Lips",
    },
    {
      id: 5,
      date: "25 Mar 2024",
      time: "10:30",
      type: "internal",
      title: "VIP Client - Special Requirements",
      content: "Patient prefers appointments early morning or late afternoon. Always request private treatment room. Complimentary refreshments to be offered. Has referred 3 new clients - consider loyalty discount on next treatment.",
      practitioner: "Dr Harper",
      isPrivate: true,
    },
    {
      id: 6,
      date: "10 Mar 2024",
      time: "13:00",
      type: "consultation",
      title: "Initial Consultation - Treatment Plan Discussion",
      content: "Patient interested in full facial rejuvenation. Discussed options including Botox for forehead lines, dermal filler for nasolabial folds, and lip enhancement. Created treatment plan spanning 3 months. Patient to start with Botox next visit.",
      practitioner: "Dr Harper",
    },
  ];

  const filteredNotes = clinicalNotes.filter((note) => {
    const matchesFilter = activeFilter === "all" || note.type === activeFilter;
    const matchesSearch = searchQuery === "" ||
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.practitioner.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Sort: Pinned notes first, then by date
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return 0;
  });

  const getTypeBadge = (type: NoteType) => {
    switch (type) {
      case "treatment":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-600">
            Treatment
          </span>
        );
      case "general":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">
            General
          </span>
        );
      case "follow-up":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-[#704aaf]">
            Follow-up
          </span>
        );
      case "consultation":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-600">
            Consultation
          </span>
        );
      case "internal":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            Internal
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#2d2d2d]">Clinical Notes</h2>
          <p className="text-sm text-[#6b6b6b] mt-1">
            Treatment notes, observations, and internal records
          </p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#5f3d9a] transition-colors"
        >
          + Add Note
        </button>
      </div>

      {/* Filter Tabs and Search */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Note Type Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeFilter === tab.id
                  ? "bg-[#704aaf] text-white"
                  : "bg-white text-[#5c5c5c] border border-[#e8e0f0] hover:bg-[#f8f5fc]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search and View Toggle */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 w-52 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {/* View Toggle */}
          <div className="flex items-center bg-[#f0ebf5] rounded-lg p-1">
            <button
              onClick={() => setViewMode("cards")}
              className={`p-2 rounded-md transition-colors ${
                viewMode === "cards" ? "bg-white shadow-sm text-[#704aaf]" : "text-gray-500"
              }`}
              title="Card View"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-md transition-colors ${
                viewMode === "list" ? "bg-white shadow-sm text-[#704aaf]" : "text-gray-500"
              }`}
              title="List View"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Notes Summary */}
      <div className="flex items-center gap-6 text-sm text-[#6b6b6b]">
        <span>{filteredNotes.length} notes</span>
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          {clinicalNotes.filter(n => n.isPinned).length} pinned
        </span>
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          {clinicalNotes.filter(n => n.isPrivate).length} private
        </span>
      </div>

      {/* Notes Content */}
      {sortedNotes.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#e8e0f0] p-12 text-center">
          <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#704aaf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-lg font-medium text-[#6b6b6b]">No notes found</p>
          <p className="text-sm text-[#9b9b9b] mt-1">Add a clinical note to start documenting</p>
        </div>
      ) : viewMode === "cards" ? (
        /* Card View */
        <div className="grid grid-cols-2 gap-4">
          {sortedNotes.map((note) => (
            <div
              key={note.id}
              className={`bg-white rounded-xl border ${
                note.isPinned ? "border-amber-200 ring-1 ring-amber-100" : "border-[#e8e0f0]"
              } p-5 hover:shadow-md transition-shadow`}
            >
              {/* Note Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {getTypeBadge(note.type)}
                  {note.isPinned && (
                    <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  )}
                  {note.isPrivate && (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button className="p-1 text-gray-400 hover:text-[#704aaf] hover:bg-purple-50 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-base font-semibold text-[#2d2d2d] mb-2">{note.title}</h3>

              {/* Content */}
              <p className="text-sm text-[#5c5c5c] mb-4 line-clamp-3">{note.content}</p>

              {/* Linked Treatment */}
              {note.linkedTreatment && (
                <div className="mb-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 text-xs text-[#704aaf]">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    {note.linkedTreatment}
                  </span>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-[#f0ebf5]">
                <span className="text-xs text-[#9b9b9b]">{note.date} at {note.time}</span>
                <span className="text-xs text-[#6b6b6b]">{note.practitioner}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#f0ebf5] bg-[#faf8fc]">
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Date</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Type</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Title</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Linked Treatment</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Practitioner</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedNotes.map((note) => (
                <tr key={note.id} className="border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc] transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      {note.isPinned && (
                        <svg className="w-3 h-3 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      )}
                      <div>
                        <p className="text-sm text-[#3d3d3d]">{note.date}</p>
                        <p className="text-xs text-[#9b9b9b]">{note.time}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">{getTypeBadge(note.type)}</td>
                  <td className="px-5 py-4">
                    <p className="text-sm font-medium text-[#2d2d2d]">{note.title}</p>
                    <p className="text-xs text-[#6b6b6b] truncate max-w-[300px]">{note.content}</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{note.linkedTreatment || "—"}</td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{note.practitioner}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors" title="View">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button className="p-1.5 text-[#704aaf] hover:bg-purple-50 rounded transition-colors" title="Edit">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button className="p-1.5 text-red-400 hover:bg-red-50 rounded transition-colors" title="Delete">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Note Modal */}
      {isAddModalOpen && (
        <AddClinicalNoteModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={(data) => {
            console.log("Note saved:", data);
          }}
        />
      )}
    </div>
  );
}

// Add Clinical Note Modal Component
interface AddClinicalNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: any) => void;
}

function AddClinicalNoteModal({ isOpen, onClose, onSave }: AddClinicalNoteModalProps) {
  const [noteType, setNoteType] = useState<NoteType>("general");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [linkedTreatment, setLinkedTreatment] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);

  const noteTypes: { id: NoteType; label: string }[] = [
    { id: "treatment", label: "Treatment" },
    { id: "general", label: "General" },
    { id: "follow-up", label: "Follow-up" },
    { id: "consultation", label: "Consultation" },
    { id: "internal", label: "Internal" },
  ];

  const treatments = [
    "Botox – Upper Face",
    "Botox – Crow's Feet",
    "Dermal Filler – Lips",
    "Dermal Filler – Cheeks",
    "Chemical Peel",
    "Microneedling",
  ];

  const handleSave = () => {
    const data = {
      type: noteType,
      title,
      content,
      linkedTreatment,
      isPinned,
      isPrivate,
      date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
      time: new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    };
    onSave?.(data);
    // Reset form
    setNoteType("general");
    setTitle("");
    setContent("");
    setLinkedTreatment("");
    setIsPinned(false);
    setIsPrivate(false);
    onClose();
  };

  const isValid = title && content;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-purple-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#2d2d2d]">Add Clinical Note</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Note Type */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">Note Type</label>
            <div className="flex flex-wrap gap-2">
              {noteTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setNoteType(type.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                    noteType === type.id
                      ? "bg-[#704aaf] text-white border-[#704aaf]"
                      : "bg-white text-[#5c5c5c] border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter note title..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Note Content <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter clinical notes..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 resize-none h-32 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>

          {/* Link to Treatment */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">Link to Treatment (Optional)</label>
            <select
              value={linkedTreatment}
              onChange={(e) => setLinkedTreatment(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            >
              <option value="">Select a treatment...</option>
              {treatments.map((treatment) => (
                <option key={treatment} value={treatment}>{treatment}</option>
              ))}
            </select>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-[#5c5c5c] cursor-pointer">
              <input
                type="checkbox"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
                className="w-4 h-4 text-[#704aaf] border-gray-300 rounded focus:ring-purple-200"
              />
              <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              Pin this note
            </label>
            <label className="flex items-center gap-2 text-sm text-[#5c5c5c] cursor-pointer">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="w-4 h-4 text-[#704aaf] border-gray-300 rounded focus:ring-purple-200"
              />
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Private note
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid}
              className="flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#5f3d9a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Gallery Tab Component
function GalleryTab() {
  const [isAddPhotosModalOpen, setIsAddPhotosModalOpen] = useState(false);

  // Static gallery data grouped by treatment - Comprehensive photo collection
  const galleryData = [
    {
      id: 1,
      treatmentCategory: "Dermal Fillers - Lips",
      entries: [
        {
          id: 1,
          beforeImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Before+0.5ml+Lip+Enhancement-640w.jpg",
          afterImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/After+0.5ml+Lip+Enhancement-640w.jpg",
          treatment: "Dermal Fillers - Lips",
          date: "17 Apr 2024",
          practitioner: "Emma Griffin",
          notes: "0.5ml Juvederm Ultra - subtle lip enhancement achieved",
        },
      ],
    },
    {
      id: 2,
      treatmentCategory: "Botox - Upper Face",
      entries: [
        {
          id: 3,
          beforeImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Before-49e05053-44482786-1920w.jpg",
          afterImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/After-d3e8c309-58a90bf2-1920w.jpg",
          treatment: "Botox - Forehead & Glabella",
          date: "20 Apr 2024",
          practitioner: "Dr. Saleema",
          notes: "32 units total - forehead lines smoothed, 11s relaxed",
        },
      ],
    },
    {
      id: 3,
      treatmentCategory: "8 Point Face Lift",
      entries: [
        {
          id: 5,
          beforeImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Eight-Point-Before-6e5526e5-f0ef9b86-1920w.jpg",
          afterImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Eight-Point-After-1920w.jpg",
          treatment: "8 Point Face Lift",
          date: "15 Mar 2024",
          practitioner: "Dr. Harper",
          notes: "Comprehensive facial rejuvenation using dermal fillers",
        },
      ],
    },
    {
      id: 4,
      treatmentCategory: "Tear Trough Treatment",
      entries: [
        {
          id: 6,
          beforeImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Under+Eye+Treatment+Before-1920w.jpg",
          afterImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Under+Eye+Treatment+After-1920w.jpg",
          treatment: "Tear Trough Treatment",
          date: "01 Mar 2024",
          practitioner: "Emma Griffin",
          notes: "Improved volume under eyes, reduced dark circles look",
        },
      ],
    },
    {
      id: 5,
      treatmentCategory: "Naso-labial Folds",
      entries: [
        {
          id: 8,
          beforeImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Naso+Labial+Before-1920w.jpg",
          afterImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Naso-Labial-After-988d22cb-1920w.jpg",
          treatment: "Naso-labial Folds",
          date: "15 Feb 2024",
          practitioner: "Dr. Harper",
          notes: "Dermal filler to soften smile lines",
        },
      ],
    },
    {
      id: 6,
      treatmentCategory: "Skin Booster",
      entries: [
        {
          id: 10,
          beforeImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/M-M+Before+-+After+skin+booster+mouth+lines+44+Website+-640w.png",
          afterImage: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/M-M+Before+-+After+Skin+Booster+under+eyes+Website+-640w.png",
          treatment: "Skin Booster Treatment",
          date: "10 Jan 2024",
          practitioner: "Dr. Saleema",
          notes: "Improved skin hydration and texture in mouth area",
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-[#704aaf]">Gallery</h2>
        <button
          onClick={() => setIsAddPhotosModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white rounded-lg text-sm font-medium hover:bg-[#5f3d9a] transition-colors"
        >
          + Add Photos
        </button>
      </div>

      {/* Add Photos Modal */}
      <AddPhotosModal
        isOpen={isAddPhotosModalOpen}
        onClose={() => setIsAddPhotosModalOpen(false)}
        onSave={(data) => {
          console.log("Photos saved:", data);
          // TODO: Handle photo save
        }}
      />

      {/* Gallery Sections by Treatment */}
      {galleryData.map((category) => (
        <div key={category.id} className="space-y-4">
          {/* Category Title */}
          <h3 className="text-base font-semibold text-[#2d2d2d] bg-[#f0ebf5] px-4 py-2 rounded-lg">
            {category.treatmentCategory}
          </h3>

          {/* Photo Grid */}
          <div className="grid grid-cols-2 gap-6">
            {category.entries.map((entry) => (
              <div key={entry.id} className="bg-white rounded-xl border border-[#e8e0f0] p-4 hover:shadow-md transition-shadow cursor-pointer">
                {/* Before/After Images */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* Before */}
                  <div>
                    <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden mb-2">
                      <img
                        src={entry.beforeImage}
                        alt="Before"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-sm font-semibold text-[#704aaf]">Before</p>
                  </div>

                  {/* After */}
                  <div>
                    <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden mb-2">
                      <img
                        src={entry.afterImage}
                        alt="After"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-sm font-semibold text-[#704aaf]">After</p>
                  </div>
                </div>

                {/* Details */}
                <div className="border-t border-[#f0ebf5] pt-3">
                  <p className="text-sm text-[#3d3d3d]">{entry.treatment}</p>
                  <p className="text-xs text-[#6b6b6b]">{entry.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Empty State */}
      {galleryData.length === 0 && (
        <div className="bg-white rounded-xl border border-[#e8e0f0] p-12 text-center">
          <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-[#704aaf]" />
          </div>
          <p className="text-lg font-medium text-[#6b6b6b]">No photos yet</p>
          <p className="text-sm text-[#9b9b9b] mt-1">Add before and after photos to track treatment progress</p>
        </div>
      )}
    </div>
  );
}

// Documents & Consent Tab Component
function DocumentsTab({ client }: { client: typeof clientData }) {
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [openActionsId, setOpenActionsId] = useState<string | null>(null);

  // Static consent forms data
  const consentForms = [
    {
      id: "c1",
      name: "Botox Consent",
      appliesTo: "Botox treatments",
      status: "pending" as const,
      dateSent: "12 Feb 2026",
      dateSigned: null,
    },
    {
      id: "c2",
      name: "Dermal Filler Consent",
      appliesTo: "Dermal fillers",
      status: "signed" as const,
      dateSent: "10 Feb 2026",
      dateSigned: "18 Feb 2026",
    },
  ];

  // Static other documents data
  const otherDocuments = [
    {
      id: "d1",
      name: "Medical History Form",
      category: "Form",
      dateAdded: "12 Feb 2026",
    },
    {
      id: "d2",
      name: "ID Document",
      category: "Form",
      dateAdded: "12 Feb 2026",
    },
  ];

  const getStatusBadge = (status: "signed" | "pending" | "not-required") => {
    switch (status) {
      case "signed":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-green-50 text-green-600 border border-green-100">
            Signed
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-orange-50 text-orange-600 border border-orange-100">
            Pending Signature
          </span>
        );
      case "not-required":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-50 text-gray-500 border border-gray-100">
            Not Required
          </span>
        );
    }
  };

  const toggleActions = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenActionsId(openActionsId === id ? null : id);
  };

  return (
    <div className="space-y-8">
      {/* Consent Forms Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#2d2d2d]">Consent Forms</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsConsentModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border border-[#e8e0f0] rounded-lg text-sm font-medium text-[#3d3d3d] bg-white hover:bg-[#f8f5fc] transition-colors"
            >
              + Add Consent Form
            </button>
            <button className="p-2 border border-[#e8e0f0] rounded-lg text-[#6b6b6b] hover:bg-[#f8f5fc] transition-colors">
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
          {/* Header */}
          <div className="bg-[#f0ebf5] px-5 py-3">
            <h3 className="text-sm font-semibold text-[#704aaf]">Consent Type</h3>
          </div>
          
          {/* Table */}
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#f0ebf5]">
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Consent Type</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Applies To</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Status</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Signed On</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {consentForms.map((consent) => (
                <tr key={consent.id} className="border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc]">
                  <td className="px-5 py-4">
                    <span className="text-sm font-medium text-[#704aaf]">{consent.name}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{consent.appliesTo}</td>
                  <td className="px-5 py-4">{getStatusBadge(consent.status)}</td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{consent.dateSigned || "-"}</td>
                  <td className="px-5 py-4 relative">
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-1.5 text-xs font-medium text-[#704aaf] bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors">
                        View
                      </button>
                      <button
                        onClick={(e) => toggleActions(consent.id, e)}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-[#704aaf] hover:bg-[#5f3d9a] rounded-lg transition-colors flex items-center gap-1"
                      >
                        Resend
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </div>
                    {openActionsId === consent.id && (
                      <div className="absolute top-full right-5 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 transition-colors">
                          Resend Email
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 transition-colors">
                          Download PDF
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors">
                          Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {consentForms.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p className="text-sm">No consent forms added yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Other Documents Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#2d2d2d]">Other Documents</h2>
          <button
            onClick={() => setIsDocumentModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 border border-[#e8e0f0] rounded-lg text-sm font-medium text-[#3d3d3d] bg-white hover:bg-[#f8f5fc] transition-colors"
          >
            + Add Document
          </button>
        </div>

        <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#f0ebf5] bg-[#f8f5fc]">
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Document Name</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Category</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Date Added</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#6b6b6b]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {otherDocuments.map((doc) => (
                <tr key={doc.id} className="border-b border-[#f0ebf5] last:border-b-0 hover:bg-[#faf8fc]">
                  <td className="px-5 py-4">
                    <span className="text-sm font-medium text-[#704aaf]">{doc.name}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{doc.category}</td>
                  <td className="px-5 py-4 text-sm text-[#5c5c5c]">{doc.dateAdded}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-1.5 text-xs font-medium text-[#704aaf] bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors">
                        View
                      </button>
                      <button className="px-3 py-1.5 text-xs font-medium text-white bg-[#704aaf] hover:bg-[#5f3d9a] rounded-lg transition-colors flex items-center gap-1">
                        Resend
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {otherDocuments.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p className="text-sm">No documents added yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {openActionsId && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setOpenActionsId(null)}
        />
      )}

      {/* Modals */}
      <AddConsentFormModal
        isOpen={isConsentModalOpen}
        onClose={() => setIsConsentModalOpen(false)}
        client={{
          name: client.name,
          email: client.email,
          avatar: client.avatar,
        }}
        onSave={(data) => {
          console.log("Consent form saved:", data);
        }}
      />

      <AddDocumentModal
        isOpen={isDocumentModalOpen}
        onClose={() => setIsDocumentModalOpen(false)}
        onSave={(data) => {
          console.log("Document saved:", data);
        }}
      />
    </div>
  );
}

// Placeholder for other tabs
function PlaceholderTab({ title }: { title: string }) {
  return (
    <div className="bg-white rounded-xl border border-[#e8e0f0] p-12 text-center">
      <p className="text-lg font-medium text-[#6b6b6b]">{title}</p>
      <p className="text-sm text-[#9b9b9b] mt-1">Content coming soon...</p>
    </div>
  );
}

